import React, { useState, useEffect, useRef } from 'react';
import { Button, CssBaseline, Typography, List, ListItem } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import { basic, Values, edu } from '../request/values';
import { formatMoneyAmount } from '../locales/formatModule';

const SERVER = basic.server;

const THEME = {
	WATER_BLUE: '#1291d9',
	PUMPKIN: '#ef7d00',
	BACKGROUND: '#f4f7fa',
	SURFACE: '#ffffff',
	TEXT_MAIN: '#333333',
	TEXT_SUB: '#888888',
};

const COUNTDOWN_MS = 1000;
const REVEAL_MS = 2800;
const REVEAL_HOLD_AFTER_FIRST_MS = 1800;

type RevealPhase = 'waiting' | 'countdown' | 'reveal' | 'done';

const useStyles = makeStyles((theme: any) => ({
	dashboardBackground: {
		width: '100vw',
		height: '100vh',
		backgroundColor: THEME.BACKGROUND,
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
		fontFamily: 'NanumSquare, NanumB, sans-serif',
	},
	mainContainer: {
		display: 'flex',
		flexDirection: 'column',
		maxWidth: '1200px',
		width: '100%',
		margin: '0 auto',
		height: '100vh',
		padding: theme.spacing(3),
		gap: theme.spacing(3),
	},
	topBar: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		padding: theme.spacing(2, 4),
		backgroundColor: THEME.SURFACE,
		borderRadius: 20,
		boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
	},
	logoSection: {
		'& img': {
			width: 180,
			height: 'auto',
		},
	},
	backButton: {
		height: 48,
		borderRadius: 12,
		backgroundColor: THEME.WATER_BLUE,
		color: '#fff',
		fontWeight: 700,
		padding: theme.spacing(0, 3),
		'&:hover': {
			backgroundColor: '#0f7bbd',
		},
	},
	contentPanel: {
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
		backgroundColor: THEME.SURFACE,
		borderRadius: 24,
		boxShadow: '0 8px 32px rgba(0,0,0,0.05)',
		overflow: 'hidden',
	},
	trophySection: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'flex-end',
		padding: theme.spacing(5, 0, 6, 0),
		gap: theme.spacing(6),
		minHeight: 280,
		background: `linear-gradient(to bottom, #ffffff, ${THEME.BACKGROUND})`,
	},
	trophyCard: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		padding: theme.spacing(2, 3),
		backgroundColor: THEME.SURFACE,
		borderRadius: 20,
		boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
		position: 'relative',
		transition: 'transform 0.3s ease',
	},
	rank1Card: {
		transform: 'scale(1.2)',
		zIndex: 2,
		border: `2px solid ${THEME.PUMPKIN}`,
		boxShadow: `0 12px 32px rgba(239, 125, 0, 0.15)`,
		marginBottom: theme.spacing(2),
	},
	rank2Card: {
		border: `2px solid #e0e0e0`,
	},
	rank3Card: {
		border: `2px solid #e0e0e0`,
	},
	trophyImg: {
		width: 75,
		height: 'auto',
		marginBottom: theme.spacing(1.5),
		filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.1))',
	},
	rank1Img: {
		width: 100,
	},
	companyNameBadge: {
		fontWeight: 800,
		fontSize: '1.2rem',
		color: THEME.TEXT_MAIN,
		textAlign: 'center',
		maxWidth: 140,
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	rank1Name: {
		color: THEME.PUMPKIN,
		fontSize: '1.4rem',
	},
	tableContainer: {
		flex: 1,
		padding: theme.spacing(0, 4, 4, 4),
		overflowY: 'auto',
		'&::-webkit-scrollbar': {
			width: '0px',
			background: 'transparent',
		},
	},
	tableHeader: {
		display: 'grid',
		gridTemplateColumns: '60px 2fr 1.5fr 30px 1.5fr 30px 1.5fr',
		padding: theme.spacing(2),
		borderBottom: `2px solid ${THEME.BACKGROUND}`,
		color: THEME.TEXT_SUB,
		fontWeight: 700,
		textAlign: 'center',
		position: 'sticky',
		top: 0,
		backgroundColor: THEME.SURFACE,
		zIndex: 10,
	},
	listItem: {
		display: 'grid',
		gridTemplateColumns: '60px 2fr 1.5fr 30px 1.5fr 30px 1.5fr',
		padding: theme.spacing(2),
		marginBottom: theme.spacing(1.5),
		backgroundColor: '#fff',
		borderRadius: 12,
		boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
		alignItems: 'center',
		textAlign: 'center',
	},
	rankBadge: {
		width: 32,
		height: 32,
		borderRadius: '50%',
		backgroundColor: THEME.BACKGROUND,
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		fontWeight: 800,
		color: THEME.TEXT_MAIN,
		margin: '0 auto',
	},
	capitalText: {
		fontWeight: 800,
		color: THEME.WATER_BLUE,
		fontSize: '1.1rem',
	},
	mathSymbol: {
		color: THEME.TEXT_SUB,
		fontWeight: 800,
	},

	// --- 공개 애니메이션 ---
	revealOverlay: {
		position: 'fixed',
		inset: 0,
		zIndex: 2000,
		backgroundColor: '#000',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		alignItems: 'center',
		fontFamily: 'NanumSquare, NanumB, sans-serif',
	},
	countdownNumber: {
		color: '#fff',
		fontSize: '14rem',
		fontWeight: 400,
		lineHeight: 1,
		animation: '$countdownPulse 1s ease-out',
		userSelect: 'none',
	},
	revealCard: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: theme.spacing(2),
		animation: '$revealIn 0.7s cubic-bezier(0.22, 1, 0.36, 1)',
		textAlign: 'center',
		padding: theme.spacing(2),
	},
	revealMedal: {
		width: 140,
		height: 'auto',
		filter: 'drop-shadow(0 8px 24px rgba(255,255,255,0.15))',
		marginBottom: theme.spacing(1),
	},
	revealMedalFirst: {
		width: 180,
	},
	revealPlace: {
		color: 'rgba(255,255,255,0.7)',
		fontSize: '1.6rem',
		fontWeight: 800,
		letterSpacing: '0.08em',
	},
	revealPlaceFirst: {
		color: THEME.PUMPKIN,
	},
	revealCompany: {
		color: '#fff',
		fontSize: '3.5rem',
		fontWeight: 900,
		maxWidth: '90vw',
		wordBreak: 'keep-all',
	},
	revealCompanyFirst: {
		color: THEME.PUMPKIN,
		fontSize: '4.2rem',
	},
	revealCapitalLabel: {
		color: 'rgba(255,255,255,0.55)',
		fontSize: '1.1rem',
		fontWeight: 700,
		marginTop: theme.spacing(1),
	},
	revealCapital: {
		color: '#fff',
		fontSize: '2.4rem',
		fontWeight: 900,
	},
	revealCapitalFirst: {
		color: THEME.PUMPKIN,
		fontSize: '2.8rem',
	},
	confettiLayer: {
		position: 'absolute',
		inset: 0,
		pointerEvents: 'none',
		overflow: 'hidden',
		zIndex: 1,
	},
	confettiBurstPiece: {
		position: 'absolute',
		top: '45%',
		left: '50%',
		opacity: 0,
		animationName: '$confettiBurst',
		animationTimingFunction: 'cubic-bezier(0.15, 0.75, 0.35, 1)',
		animationFillMode: 'forwards',
	},
	confettiFallPiece: {
		position: 'absolute',
		top: '-8%',
		opacity: 0,
		animationName: '$confettiFall',
		animationTimingFunction: 'linear',
		animationFillMode: 'forwards',
	},
	'@keyframes countdownPulse': {
		'0%': {
			opacity: 0,
			transform: 'scale(1.6)',
		},
		'40%': {
			opacity: 1,
			transform: 'scale(1)',
		},
		'100%': {
			opacity: 1,
			transform: 'scale(1)',
		},
	},
	'@keyframes revealIn': {
		'0%': {
			opacity: 0,
			transform: 'scale(0.7) translateY(40px)',
		},
		'100%': {
			opacity: 1,
			transform: 'scale(1) translateY(0)',
		},
	},
	'@keyframes confettiBurst': {
		'0%': {
			opacity: 1,
			transform: 'translate(-50%, -50%) scale(1) rotate(0deg)',
		},
		'100%': {
			opacity: 0,
			transform: 'translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(0.4) rotate(var(--rot))',
		},
	},
	'@keyframes confettiFall': {
		'0%': {
			opacity: 1,
			transform: 'translate3d(0, 0, 0) rotate(0deg)',
		},
		'80%': {
			opacity: 1,
		},
		'100%': {
			opacity: 0,
			transform: 'translate3d(var(--drift), 115vh, 0) rotate(var(--rot))',
		},
	},
}));

const getTotalCapital = (item: any) => (item?.capital || 0) + (item?.license_stock || 0);

const MEDAL_BY_PLACE: Record<number, string> = {
	1: '/images/ranking/gold.webp',
	2: '/images/ranking/silver.webp',
	3: '/images/ranking/bronze.webp',
};

const CONFETTI_BY_PLACE: Record<1 | 2 | 3, { burstCount: number; fallCount: number; colors: string[]; burstDurationMs: number; fallDurationMs: number }> = {
	3: {
		burstCount: 36,
		fallCount: 28,
		colors: ['#cd7f32', '#e8a86a', '#ffffff', '#f0c090'],
		burstDurationMs: 1800,
		fallDurationMs: 2600,
	},
	2: {
		burstCount: 64,
		fallCount: 48,
		colors: ['#c0c0c0', '#e8e8e8', '#ffffff', '#9aa3ad', '#1291d9'],
		burstDurationMs: 2200,
		fallDurationMs: 3000,
	},
	1: {
		burstCount: 120,
		fallCount: 90,
		colors: ['#ef7d00', '#ffd700', '#fff3bf', '#ffffff', '#ff9f1a', '#ffec8a'],
		burstDurationMs: 2800,
		fallDurationMs: 3600,
	},
};

type BurstPiece = {
	kind: 'burst';
	id: string;
	color: string;
	dx: number;
	dy: number;
	rot: number;
	width: number;
	height: number;
	delay: number;
	duration: number;
	radius: number;
};

type FallPiece = {
	kind: 'fall';
	id: string;
	color: string;
	left: number;
	drift: number;
	rot: number;
	width: number;
	height: number;
	delay: number;
	duration: number;
	radius: number;
};

type ConfettiPiece = BurstPiece | FallPiece;

const buildConfettiPieces = (place: 1 | 2 | 3): ConfettiPiece[] => {
	const config = CONFETTI_BY_PLACE[place];
	const pieces: ConfettiPiece[] = [];

	for (let i = 0; i < config.burstCount; i += 1) {
		const angle = (Math.PI * 2 * i) / config.burstCount + (Math.random() - 0.5) * 0.6;
		const distance = (place === 1 ? 280 : place === 2 ? 220 : 160) * (0.55 + Math.random() * 0.7);
		const width = place === 1 ? 8 + Math.random() * 10 : 6 + Math.random() * 8;
		pieces.push({
			kind: 'burst',
			id: `burst-${i}`,
			color: config.colors[i % config.colors.length],
			dx: Math.cos(angle) * distance + (Math.random() - 0.5) * 80,
			dy: Math.sin(angle) * distance * 0.85 - 40 - Math.random() * 120,
			rot: (Math.random() > 0.5 ? 1 : -1) * (180 + Math.random() * 540),
			width,
			height: width * (0.45 + Math.random() * 0.7),
			delay: Math.random() * (place === 1 ? 0.35 : 0.2),
			duration: (config.burstDurationMs / 1000) * (0.75 + Math.random() * 0.4),
			radius: Math.random() > 0.55 ? 50 : 2,
		});
	}

	// 1등은 중앙 폭죽을 한 번 더 강하게
	if (place === 1) {
		for (let i = 0; i < 40; i += 1) {
			const width = 7 + Math.random() * 9;
			pieces.push({
				kind: 'burst',
				id: `burst-extra-${i}`,
				color: config.colors[i % config.colors.length],
				dx: (Math.random() - 0.5) * 900,
				dy: -280 - Math.random() * 420,
				rot: (Math.random() > 0.5 ? 1 : -1) * (220 + Math.random() * 500),
				width,
				height: width * (0.45 + Math.random() * 0.7),
				delay: 0.15 + Math.random() * 0.45,
				duration: 2.2 + Math.random() * 0.8,
				radius: Math.random() > 0.5 ? 50 : 2,
			});
		}
	}

	// 위에서 내려오는 꽃가루
	for (let i = 0; i < config.fallCount; i += 1) {
		const width = place === 1 ? 7 + Math.random() * 9 : 5 + Math.random() * 7;
		pieces.push({
			kind: 'fall',
			id: `fall-${i}`,
			color: config.colors[i % config.colors.length],
			left: Math.random() * 100,
			drift: (Math.random() - 0.5) * (place === 1 ? 180 : 120),
			rot: (Math.random() > 0.5 ? 1 : -1) * (260 + Math.random() * 720),
			width,
			height: width * (0.4 + Math.random() * 0.9),
			delay: Math.random() * (place === 1 ? 1.2 : place === 2 ? 0.9 : 0.6),
			duration: (config.fallDurationMs / 1000) * (0.85 + Math.random() * 0.45),
			radius: Math.random() > 0.45 ? 50 : 2,
		});
	}

	return pieces;
};

const RevealConfetti: React.FC<{
	place: 1 | 2 | 3;
	className: string;
	burstClassName: string;
	fallClassName: string;
}> = ({ place, className, burstClassName, fallClassName }) => {
	const pieces = React.useMemo(() => buildConfettiPieces(place), [place]);

	return (
		<div className={className} aria-hidden>
			{pieces.map((piece) =>
				piece.kind === 'burst' ? (
					<span
						key={piece.id}
						className={burstClassName}
						style={
							{
								'--dx': `${piece.dx}px`,
								'--dy': `${piece.dy}px`,
								'--rot': `${piece.rot}deg`,
								width: piece.width,
								height: piece.height,
								backgroundColor: piece.color,
								borderRadius: piece.radius,
								animationDuration: `${piece.duration}s`,
								animationDelay: `${piece.delay}s`,
							} as React.CSSProperties
						}
					/>
				) : (
					<span
						key={piece.id}
						className={fallClassName}
						style={
							{
								left: `${piece.left}%`,
								'--drift': `${piece.drift}px`,
								'--rot': `${piece.rot}deg`,
								width: piece.width,
								height: piece.height,
								backgroundColor: piece.color,
								borderRadius: piece.radius,
								animationDuration: `${piece.duration}s`,
								animationDelay: `${piece.delay}s`,
							} as React.CSSProperties
						}
					/>
				)
			)}
		</div>
	);
};

const OperOpenRanking: React.FC<any> = (props) => {
	const classes = useStyles();
	const { oper } = props;
	const { t } = useTranslation('oper');

	const [rank, setRank] = useState<any[]>([]);
	const [rankLoaded, setRankLoaded] = useState(false);
	const [phase, setPhase] = useState<RevealPhase>('waiting');
	const [countdown, setCountdown] = useState(3);
	const [revealPlace, setRevealPlace] = useState<1 | 2 | 3 | null>(null);

	const history = useHistory();
	const state: any = history.location.state;
	const classCode = state?.code;
	const listRef = useRef<HTMLDivElement>(null);

	const getRankContent = () => {
		axios({
			method: 'POST',
			data: {
				class_code: classCode,
				classVersion: oper.classVersion,
			},
			withCredentials: true,
			url: SERVER + edu.path + edu.class_rank_content,
		})
			.then((res) => res.data)
			.then((data: any) => {
				if (data.code == Values.SUCCESS_CODE) {
					setRank(data.rank || []);
					setRankLoaded(true);
				}
			});
	};

	useEffect(() => {
		getRankContent();
	}, []);

	// --- 공개 애니메이션 시퀀스: 3 → 2 → 1 카운트 → 3등 → 2등 → 1등 → 최종 화면 ---
	useEffect(() => {
		if (!rankLoaded) return;

		const timers: NodeJS.Timeout[] = [];
		const schedule = (fn: () => void, delay: number) => {
			timers.push(setTimeout(fn, delay));
		};

		setPhase('countdown');
		setCountdown(3);

		schedule(() => setCountdown(2), COUNTDOWN_MS);
		schedule(() => setCountdown(1), COUNTDOWN_MS * 2);

		schedule(() => {
			setPhase('reveal');
			setRevealPlace(3);
		}, COUNTDOWN_MS * 3);

		schedule(() => setRevealPlace(2), COUNTDOWN_MS * 3 + REVEAL_MS);
		schedule(() => setRevealPlace(1), COUNTDOWN_MS * 3 + REVEAL_MS * 2);
		schedule(() => {
			setPhase('done');
			setRevealPlace(null);
		}, COUNTDOWN_MS * 3 + REVEAL_MS * 3 + REVEAL_HOLD_AFTER_FIRST_MS);

		return () => timers.forEach(clearTimeout);
	}, [rankLoaded]);

	// --- 최종 화면에서만 리스트 자동 스크롤 ---
	useEffect(() => {
		if (phase !== 'done') return;

		let scrollInterval: NodeJS.Timeout;
		if (listRef.current) {
			scrollInterval = setInterval(() => {
				if (listRef.current) {
					const { scrollTop, scrollHeight, clientHeight } = listRef.current;
					if (scrollTop + clientHeight >= scrollHeight - 1) {
						listRef.current.scrollTop = 0;
					} else {
						listRef.current.scrollTop += 1;
					}
				}
			}, 50);
		}

		return () => clearInterval(scrollInterval);
	}, [phase, rank]);

	const movePage = () => {
		history.push({ pathname: '/' });
	};

	const revealedItem = revealPlace != null ? rank[revealPlace - 1] : null;

	if (phase !== 'done') {
		return (
			<div className={classes.revealOverlay} data-testid="oper-ranking-reveal-animation">
				<CssBaseline />
				{phase === 'countdown' && (
					<div key={countdown} className={classes.countdownNumber}>
						{countdown}
					</div>
				)}
				{phase === 'reveal' && revealedItem && revealPlace != null && (
					<>
						<RevealConfetti
							key={`confetti-${revealPlace}`}
							place={revealPlace}
							className={classes.confettiLayer}
							burstClassName={classes.confettiBurstPiece}
							fallClassName={classes.confettiFallPiece}
						/>
						<div key={revealPlace} className={classes.revealCard} style={{ position: 'relative', zIndex: 2 }}>
							<img src={MEDAL_BY_PLACE[revealPlace]} alt={`${revealPlace}`} className={`${classes.revealMedal} ${revealPlace === 1 ? classes.revealMedalFirst : ''}`} />
							<Typography className={`${classes.revealPlace} ${revealPlace === 1 ? classes.revealPlaceFirst : ''}`}>{t('OperOpenRanking.placeLabel', { n: revealPlace })}</Typography>
							<Typography className={`${classes.revealCompany} ${revealPlace === 1 ? classes.revealCompanyFirst : ''}`}>{revealedItem.company_name || '-'}</Typography>
							<Typography className={classes.revealCapitalLabel}>{t('OperOpenRanking.totalCapital')}</Typography>
							<Typography className={`${classes.revealCapital} ${revealPlace === 1 ? classes.revealCapitalFirst : ''}`}>{formatMoneyAmount(getTotalCapital(revealedItem))}</Typography>
						</div>
					</>
				)}
			</div>
		);
	}

	return (
		<div className={classes.dashboardBackground}>
			<CssBaseline />
			<div className={classes.mainContainer}>
				<div className={classes.topBar}>
					<div className={classes.logoSection}>
						<img src="/images/ranking/logo.webp" alt="Play Startup Logo" />
					</div>
					<Button onClick={movePage} className={classes.backButton}>
						{t('OperOpenRanking.backToMain')}
					</Button>
				</div>

				<div className={classes.contentPanel}>
					<div className={classes.trophySection}>
						<div className={`${classes.trophyCard} ${classes.rank2Card}`}>
							<img src="/images/ranking/silver.webp" className={classes.trophyImg} alt="Silver Medal" />
							<div className={classes.companyNameBadge}>{rank[1]?.company_name || '-'}</div>
						</div>
						<div className={`${classes.trophyCard} ${classes.rank1Card}`}>
							<img src="/images/ranking/gold.webp" className={`${classes.trophyImg} ${classes.rank1Img}`} alt="Gold Medal" />
							<div className={`${classes.companyNameBadge} ${classes.rank1Name}`}>{rank[0]?.company_name || '-'}</div>
						</div>
						<div className={`${classes.trophyCard} ${classes.rank3Card}`}>
							<img src="/images/ranking/bronze.webp" className={classes.trophyImg} alt="Bronze Medal" />
							<div className={classes.companyNameBadge}>{rank[2]?.company_name || '-'}</div>
						</div>
					</div>

					<div className={classes.tableContainer} ref={listRef}>
						<div className={classes.tableHeader}>
							<div>{t('OperOpenRanking.rank')}</div>
							<div>{t('OperOpenRanking.companyName')}</div>
							<div>{t('OperOpenRanking.asset')}</div>
							<div></div>
							<div>{t('OperOpenRanking.debt')}</div>
							<div></div>
							<div>{t('OperOpenRanking.capital')}</div>
						</div>
						<List disablePadding>
							{rank.map((item: any, index: number) => (
								<AppListItem
									key={index}
									index={index}
									name={item.company_name}
									stock={item.stock}
									loan={item.loan}
									loan_interest={item.loan_interest}
									capital={item.capital}
									material_stock={item.material_stock}
									material_loan_stock={item.material_loan_stock}
									license_stock={item.license_stock}
								/>
							))}
						</List>
					</div>
				</div>
			</div>
		</div>
	);
};

const AppListItem: React.FC<any> = (props) => {
	const classes = useStyles();
	const { index, name, stock, loan, loan_interest, capital, material_stock, material_loan_stock, license_stock } = props;

	return (
		<ListItem className={classes.listItem}>
			<div className={classes.rankBadge}>{index + 1}</div>
			<div style={{ fontWeight: 700 }}>{name}</div>
			<div>{formatMoneyAmount(stock + (material_stock || 0) + (license_stock || 0))}</div>
			<div className={classes.mathSymbol}>-</div>
			<div>{formatMoneyAmount(loan + loan_interest + (material_loan_stock || 0))}</div>
			<div className={classes.mathSymbol}>=</div>
			<div className={classes.capitalText}>{formatMoneyAmount(capital + (license_stock || 0))}</div>
		</ListItem>
	);
};

export default OperOpenRanking;
